import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Highlighter, ImageIcon, StickyNote, Trash2 } from "lucide-react";
import { useHighlights } from "@/lib/highlights";
import { Loader } from "./Common";
import { VerseCardDialog } from "./VerseCard";

export function HighlightButton({ refText, text, title, link, kids }: { refText: string; text: string; title: string; link: string; kids?: boolean }) {
  const { items, find, save, remove } = useHighlights();
  const saved = find(refText, text);
  return (
    <button data-testid="verse-highlight-button" disabled={!items} aria-pressed={!!saved}
      onClick={() => (saved ? remove(saved.id) : save(refText, text, title, link))}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-semibold not-italic transition-all hover:scale-105 ${kids ? "text-sm" : "text-xs"} ${saved ? "bg-amber-300 text-amber-950 shadow" : "bg-white/80 text-amber-900 ring-1 ring-amber-300"}`}>
      <Highlighter className="h-3.5 w-3.5" />{saved ? (kids ? "Saved!" : "Saved") : kids ? "Save this verse" : "Highlight"}
    </button>
  );
}

function VerseNote({ h, kids }: { h: any; kids?: boolean }) {
  const { setNote } = useHighlights();
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(h.note || "");
  if (!edit) return h.note ? (
    <button onClick={() => setEdit(true)} data-testid={`saved-verse-note-${h.id}`} className="mt-3 flex w-full items-start gap-2 rounded-xl bg-white/80 px-3 py-2 text-left text-sm text-slate-700 ring-1 ring-amber-200 hover:bg-white">
      <StickyNote className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /><span className="font-hand">{h.note}</span>
    </button>
  ) : (
    <button onClick={() => setEdit(true)} data-testid={`saved-verse-add-note-${h.id}`} className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-800 hover:underline"><StickyNote className="h-3.5 w-3.5" />{kids ? "Write why you love it" : "Add a note"}</button>
  );
  const save = async () => { await setNote(h.id, val); setEdit(false); };
  return (
    <div className="mt-3 space-y-2">
      <textarea data-testid={`saved-verse-note-input-${h.id}`} value={val} onChange={(e) => setVal(e.target.value.slice(0, 280))} rows={2} autoFocus
        placeholder={kids ? "I love this verse because…" : "Why this verse matters to you…"} className="w-full rounded-xl border border-amber-200 bg-white p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" />
      <div className="flex items-center gap-2 text-xs">
        <span className="text-slate-400">{val.length}/280</span><span className="flex-1" />
        <button onClick={() => { setVal(h.note || ""); setEdit(false); }} className="rounded-full px-3 py-1 font-semibold text-slate-500 hover:bg-slate-100">Cancel</button>
        <button onClick={save} data-testid={`saved-verse-note-save-${h.id}`} className="rounded-full bg-amber-500 px-3 py-1 font-semibold text-white hover:bg-amber-600">Save note</button>
      </div>
    </div>
  );
}

export function VerseCollection({ kids }: { kids?: boolean }) {
  const { items, remove } = useHighlights();
  const [card, setCard] = useState<any>(null);
  if (!items) return <Loader />;
  if (!items.length) return (
    <div className="rounded-2xl border border-dashed border-amber-300 bg-amber-50/50 p-8 text-center" data-testid="verses-empty">
      <Highlighter className="mx-auto h-8 w-8 text-amber-500" />
      <p className={`mt-3 font-semibold text-slate-800 ${kids ? "text-xl" : ""}`}>No saved verses yet</p>
      <p className="mt-1 text-sm text-slate-500">Tap {kids ? "“Save this verse”" : "“Highlight”"} on any verse in a story{kids ? "" : " or study"} to keep it here.</p>
    </div>
  );
  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="verse-collection">
      {items.map((h, i) => (
        <motion.figure key={h.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 8) * 0.05 }}
          className="relative rounded-2xl border-l-4 border-amber-500 bg-gradient-to-br from-amber-50 to-white p-5 shadow-sm" data-testid={`saved-verse-${h.id}`}>
          <blockquote className={`font-scripture italic leading-relaxed text-amber-950 ${kids ? "text-xl" : "text-lg"}`}><mark className="bg-amber-200/60 px-0.5 text-inherit">“{h.text}”</mark></blockquote>
          <figcaption className="mt-3 flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-full bg-amber-100 px-2.5 py-0.5 font-semibold text-amber-900">{h.ref} · {h.version}</span>
            {h.link ? <Link to={h.link} className="font-semibold text-[#C86D51] hover:underline" data-testid={`saved-verse-source-${h.id}`}>{h.source_title}</Link> : <span className="text-slate-500">{h.source_title}</span>}
            <button onClick={() => setCard(h)} data-testid={`saved-verse-card-${h.id}`} className="ml-auto inline-flex items-center gap-1 rounded-full bg-[#1E293B] px-3 py-1 font-semibold text-white transition-transform hover:scale-105"><ImageIcon className="h-3.5 w-3.5" />{kids ? "Make a picture" : "Picture card"}</button>
            <button onClick={() => remove(h.id)} data-testid={`saved-verse-remove-${h.id}`} aria-label="Remove verse" className="rounded-full p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
          </figcaption>
          <VerseNote h={h} kids={kids} />
        </motion.figure>
      ))}
      {card && <VerseCardDialog verse={card} open={!!card} onOpenChange={(o) => !o && setCard(null)} />}
    </div>
  );
}
